const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const SENSITIVE_QUERY_RE =
  /^(token|password|passwd|secret|email|mail|authorization|auth|jwt|code|session|sid|api[_-]?key|access[_-]?token|refresh[_-]?token|id_token|client_secret)$/i;

const MAX_DEPTH = 6;

/**
 * Object keys that must survive redaction. Faro's session instrumentation reads
 * `meta.session.attributes.isSampled` *after* `beforeSend` and drops every signal
 * when it is missing, so redacting this key silences the whole SDK. Session ids
 * are random correlation ids, not credentials.
 */
const PRESERVED_KEYS = new Set(['session']);

function stripSensitiveQuery(url: string): string {
  try {
    const parsed = new URL(url, 'https://baslerweb.com');
    const keys = [...parsed.searchParams.keys()];
    for (const key of keys) {
      if (SENSITIVE_QUERY_RE.test(key)) {
        parsed.searchParams.delete(key);
      }
    }
    parsed.hash = '';
    const result = parsed.toString();
    if (url.startsWith('/') || !url.includes('://')) {
      return `${parsed.pathname}${parsed.search}`;
    }
    return result;
  } catch {
    return url.split('#')[0]?.split('?')[0] ?? url;
  }
}

function sanitizeString(value: string): string {
  const withoutEmail = value.replace(EMAIL_RE, '[email]');
  if (/^https?:\/\//i.test(withoutEmail) || withoutEmail.startsWith('/')) {
    return stripSensitiveQuery(withoutEmail);
  }
  return withoutEmail;
}

function sanitizeValue(value: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH || value == null) {
    return value;
  }
  if (typeof value === 'string') {
    return sanitizeString(value);
  }
  if (Array.isArray(value)) {
    return value.map((entry: unknown) => sanitizeValue(entry, depth + 1));
  }
  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(source)) {
      if (!PRESERVED_KEYS.has(key) && SENSITIVE_QUERY_RE.test(key)) {
        next[key] = '[redacted]';
        continue;
      }
      next[key] = sanitizeValue(entry, depth + 1);
    }
    return next;
  }
  return value;
}

/**
 * Drops emails, auth-ish query params, and fragments from Faro beacons.
 */
export function sanitizeFaroPayload<T>(item: T): T {
  return sanitizeValue(item, 0) as T;
}
